import { ThunkAction } from 'redux-thunk'
import { AnyAction } from '@reduxjs/toolkit'
import BigNumber from 'bignumber.js'
import {
  FarmConfig,
  VaultConfig,
  PoolConfig,
  SeriesConfig,
  DNft,
  MultiPoolConfig,
  Currency,
  SeriesKey,
} from 'config/constants/types'

export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, State, unknown, AnyAction>

export interface BigNumberToJson {
  type: 'BigNumber'
  hex: string
}

export type TranslatableText =
  | string
  | {
      key: string
      data?: {
        [key: string]: string | number
      }
    }

export type SerializedBigNumber = string

export interface Farm extends FarmConfig {
  tokenAmountMc?: SerializedBigNumber
  quoteTokenAmountMc?: SerializedBigNumber
  tokenAmountTotal?: SerializedBigNumber
  quoteTokenAmountTotal?: SerializedBigNumber
  lpTotalInQuoteToken?: SerializedBigNumber
  lpTotalSupply?: SerializedBigNumber
  tokenPriceVsQuote?: SerializedBigNumber
  poolWeight?: SerializedBigNumber
  userData?: {
    allowance: string
    tokenBalance: string
    stakedBalance: string
    earnings: string
  }
}

export interface Vault extends VaultConfig {
  tokenAmountTotal?: BigNumber
  userData?: {
    allowance: string
    tokenBalance: string
    sharesBalance: string
    lastDepositedTime?: string
  }
  pricePerFullShare?: BigNumber
  tokenPrice?: number
  apy?: number
  daily?: number
  tvl?: BigNumber
  // lastDepositPricePerfullShare?: string
}

export interface Series extends SeriesConfig {
  price?: number
  maxMintPerTx?: number
  onlyWhitelisted?: boolean
  startStakingTime?: number
  tokenPerSecond?: number
  rewardsEndDate?: string
  maxSupply?: number
  marketPrice?: number
  minted?: number
  totalStaked?: number
  totalStakedAbility?: number
  paused?: boolean
  stakingPaused?: boolean
  apr?: number
  rewardTokenPrice?: number
  rewardRemained?: number
  isFinished?: boolean
  userData?: {
    // balance?: number
    nfts?: DNft[]
    earned?: number
    approved?: boolean
    isWhitelisted?: boolean
    tokenAllowance: BigNumber
  }
}

export interface Pool extends PoolConfig {
  totalStaked?: BigNumber
  stakingLimit?: BigNumber
  startBlock?: number
  endBlock?: number
  apr?: number
  stakingTokenPrice?: number
  earningTokenPrice?: number
  userData?: {
    allowance: BigNumber
    stakingTokenBalance: BigNumber
    stakedBalance: BigNumber
    pendingReward: BigNumber
  }
}

export interface MultiPool extends MultiPoolConfig {
  earningTokensApr?: number[]
  tvlUsd?: BigNumber
  userData?: {
    allowance?: BigNumber
    stakedBalance?: BigNumber
    earns?: {
      tokenValue?: number
      usdVaule?: number
    }[]
  }
}

export interface MultiPoolState {
  data: MultiPool
  userDataLoaded: boolean
}

export interface Launchpad {
  v1: {
    onlyWhitelisted: boolean
    owner: string
  }
  v2: {
    onlyWhitelisted: boolean
    owner: string
    onlyPayDMD: boolean
    onlyPayFTM: boolean
    feeFTM: BigNumber
    feeDMD: BigNumber
  }
  newCollectionAddress?: string
  userData?: {
    v1: {
      isWhitelisted?: boolean
    }
    v2: {
      isWhitelisted?: boolean
      allowance?: BigNumber
    }
  }
}

export interface LaunchpadState {
  data: Launchpad
  userDataLoaded: boolean
}

// Slices states

export interface FarmsState {
  data: Farm[]
  loadArchivedFarmsData: boolean
  userDataLoaded: boolean
  prices: any
}

export interface VaultsState {
  data: Vault[]
  userDataLoaded: boolean
}

export enum Field {
  INPUT = 'INPUT',
  OUTPUT = 'OUTPUT',
}

export interface ZapState {
  readonly independentField?: Field
  readonly typedValue?: string
  readonly [Field.INPUT]?: {
    readonly currencyId: string | undefined
  }
  readonly [Field.OUTPUT]?: {
    readonly currencyId: string | undefined
  }
  // the typed recipient address or ENS name, or null if swap should go to sender
  readonly recipient?: string | null
  tokenBalances: Currency[]
  userDataLoaded: boolean
}

export interface SeriesesState {
  data: Series[]
  userDataLoaded: boolean
}

export interface PresaleState {
  presaleStartTimestamp?: SerializedBigNumber
  presaleEndTimestamp?: SerializedBigNumber
  hardCapEthAmount?: SerializedBigNumber
  totalDepositedEthBalance?: SerializedBigNumber
  minimumDepositEthAmount?: SerializedBigNumber
  maximumDepositEthAmount?: SerializedBigNumber
  userDataLoaded?: boolean
  userData?: {
    balance: SerializedBigNumber
    deposits: SerializedBigNumber
  }
}

export interface VaultFees {
  performanceFee: number
  callFee: number
  withdrawalFee: number
  withdrawalFeePeriod: number
}

export interface VaultUser {
  isLoading: boolean
  userShares: string
  cakeAtLastUserAction: string
  lastDepositedTime: string
  lastUserActionTime: string
}

export interface PoolsState {
  data: Pool[]
  userDataLoaded: boolean
}

// Block

export interface BlockState {
  currentBlock: number
  initialBlock: number
}

// Auth

export interface AuthState {
  credential: any
}

// ApiServicesState
export interface PricePerfullShareHistory {
  timestamp: string
  vaules: any
}

export interface ApiServicesState {
  pricePerfullSharesHistory: {
    data: PricePerfullShareHistory[]
    dataLoaded: boolean
  }
}

export interface Wheel {
  endTime: number
  isOpened: boolean
  maxBuyTicket: number
  bribeFee?: BigNumber
  ticketPrice?: BigNumber
  lastWinners: string[]
  totalBurned?: BigNumber
  randomNumber?: number
  prizePoolBalance: BigNumber
  userData?: {
    isSers?: boolean
    dmdBalance?: BigNumber
    allowance?: BigNumber
    tickets?: number
    pendingReward?: BigNumber
    purchasedNumbers?: number[]
  }
}

export interface WheelState {
  data: Wheel
  userDataLoaded: boolean
}

export interface UserCollection {
  key: SeriesKey
  name: string
  image: string
  animation: string
  address: string
  nfts?: DNft[]
}

export interface UserCollectionsState {
  data: UserCollection[]
  nftsFromMoralis: any[]
  userDataLoaded: boolean
}

// Global state

export interface State {
  block: BlockState
  apiServices: ApiServicesState
  farms: FarmsState
  vaults: VaultsState
  presale: PresaleState
  pools: PoolsState
  multiPool: MultiPoolState
  launchpad: LaunchpadState
  serieses: SeriesesState
  zap: ZapState
  wheel: WheelState
  userCollections: UserCollectionsState
}
